import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import { getInvoicesByClient } from "../services/invoicesService";
import {
  getBillingSubscriptionPlans,
  updateBillingClientPlan,
} from "../services/subscriptionPlansService";
import {
  createPaymentMethod,
  deletePaymentMethod,
  getPaymentMethodById,
  getPaymentMethods,
} from "../services/paymentMethodsService";
import { detectCardBrandKey } from "../constants/cardBrand";

const toTitleCase = (value) => {
  if (!value) return "Draft";
  const text = String(value);
  return text
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatAmount = (amount) => {
  const amountNumber = Number(amount);
  if (Number.isNaN(amountNumber)) return "$0.00";
  return `$${amountNumber.toFixed(2)}`;
};

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n", ""].includes(normalized)) return false;
  }
  return Boolean(value);
};

const isCustomPlanRecord = (plan) => {
  const planName = String(plan?.plan_name || "").trim().toLowerCase();
  const planType = String(plan?.plan_type || "").trim().toLowerCase();
  return Number(plan?.id) === 5 || planName === "custom" || planType === "custom";
};

const toPlanFeatures = (plan) => {
  const features = [];
  const planName = String(plan?.plan_name || "").trim().toLowerCase();
  const planType = String(plan?.plan_type || "").trim().toLowerCase();
  const isBasicPlan =
    planName === "basic" || planType === "basic" || Number(plan?.id) === 1;

  if (toNumber(plan.plan_voicemails, 0) > 0) {
    features.push(`Up to ${plan.plan_voicemails} voicemails per month`);
  }

  if (plan.plan_email_delivery || plan.plan_sms_delivery) {
    features.push("Email/SMS delivery");
  }

  if (plan.plan_voicebox) {
    features.push("Voicemail box option*");
  }

  const hasPrioritySupport = !isBasicPlan && toBoolean(plan.plan_support);
  features.push(hasPrioritySupport ? "Priority support" : "Basic support");
  return features;
};

const normalizePlan = (plan) => {
  const id = toNumber(plan?.id, null);
  if (!id) return null;

  const isCustomPlan = isCustomPlanRecord(plan);

  if (isCustomPlan) {
    const voicemailCount = toNumber(plan?.plan_voicemails, 0);
    const customFeatures = [
      voicemailCount > 0
        ? `More than ${voicemailCount} voicemails per month`
        : "More than 1500 voicemails per month",
      toBoolean(plan?.plan_email_delivery) || toBoolean(plan?.plan_sms_delivery)
        ? "Email/SMS delivery"
        : null,
      toBoolean(plan?.plan_voicebox) ? "Voicemail box option*" : null,
      toBoolean(plan?.plan_support) ? "Account Manager" : "Basic support",
    ].filter(Boolean);

    return {
      id,
      name: "Custom",
      price: toNumber(plan?.plan_monthly_amount, 0) || null,
      annualPrice: toNumber(plan?.plan_yearly_amount, 0) || null,
      voicemailLimit: voicemailCount,
      voicemails: voicemailCount > 0 ? `>${voicemailCount}` : ">1500",
      features: customFeatures,
      cta: "CONTACT SALES",
      isCustom: true,
    };
  }

  const monthlyAmount = toNumber(plan.plan_monthly_amount, 0);
  const yearlyAmount = toNumber(plan.plan_yearly_amount, 0);
  const voicemailCount = toNumber(plan.plan_voicemails, 0);

  return {
    id,
    name: plan.plan_name || "Unnamed",
    price: monthlyAmount,
    annualPrice: yearlyAmount,
    voicemailLimit: voicemailCount,
    voicemails:
      voicemailCount > 0
        ? `${voicemailCount} voicemails`
        : "Voicemail allowance unavailable",
    features: toPlanFeatures(plan),
    cta: "UPGRADE PLAN",
    isCustom: false,
  };
};

const formatExpiryForDisplay = (rawValue) => {
  if (!rawValue) return "N/A";

  const parsedDate = new Date(rawValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = String(parsedDate.getFullYear()).slice(-2);
    return `${month}/${year}`;
  }

  const matched = String(rawValue).match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (matched) {
    return `${matched[2]}/${matched[1].slice(-2)}`;
  }

  return String(rawValue);
};

const normalizePaymentMethods = (responseData) => {
  const list = responseData?.paymentMethods || [];
  if (!Array.isArray(list)) return [];

  return list.map((item) => ({
    id: item.id,
    brand: detectCardBrandKey({
      cardNumber: item.card_number,
      brand: item.card_brand,
    }),
    last4: String(item.card_number || "").slice(-4),
    exp: formatExpiryForDisplay(item.expiry_date),
    default: Boolean(item.is_default),
    cardholderName: item.cardholder_name || "",
    cardStatus: item.card_status || "active",
  }));
};

const normalizePaymentMethodDetails = (item = {}) => {
  const rawCardNumber = String(item.card_number || "");
  const last4 = rawCardNumber.slice(-4) || "N/A";

  return {
    id: item.id,
    cardholderName: item.cardholder_name || "",
    maskedCardNumber: `•••• •••• •••• ${last4}`,
    expiryDate: formatExpiryForDisplay(item.expiry_date),
    cardStatus: String(item.card_status || "active").toLowerCase(),
  };
};

export const fetchInvoicesThunk = createAsyncThunk(
  "billing/fetchInvoices",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getInvoicesByClient(resolvedClientId, {
      limit: 20,
      offset: 0,
    });

    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load invoices.");
    }

    const apiInvoices = Array.isArray(response?.invoices) ? response.invoices : [];
    const mappedInvoices = apiInvoices.map((invoice) => ({
      id: invoice.invoice_name || `INV-${invoice.id}`,
      date: formatDate(invoice.invoice_date),
      plan: invoice.invoice_type || "N/A",
      amount: formatAmount(invoice.invoice_amount),
      status: toTitleCase(invoice.invoice_status || invoice.status),
      invoiceFileUrl: invoice.invoice_file_url,
    }));

    return {
      invoices: mappedInvoices,
      totalInvoices: Number(response?.total || mappedInvoices.length),
    };
  },
);

export const fetchBillingPlansThunk = createAsyncThunk(
  "billing/fetchPlans",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getBillingSubscriptionPlans(resolvedClientId, {
      limit: 20,
      offset: 0,
    });

    const rawPlans = Array.isArray(response?.plans) ? response.plans : [];
    const normalized = rawPlans
      .map(normalizePlan)
      .filter(Boolean)
      .sort((a, b) => toNumber(a.id, 0) - toNumber(b.id, 0));

    const deduped = Array.from(
      normalized.reduce((acc, item) => acc.set(item.id, item), new Map()).values(),
    );

    if (!deduped.length || response?.plansError) {
      return rejectWithValue(response?.plansError || "Unable to load subscription plans.");
    }

    return {
      plans: deduped,
      currentClient: response?.currentClient || null,
      currentPlanId: response?.currentPlanId || null,
      clientError: response?.clientError || "",
    };
  },
);

export const updateBillingPlanThunk = createAsyncThunk(
  "billing/updatePlan",
  async (plan, { getState, rejectWithValue }) => {
    const state = getState().billing;

    if (!plan || !plan.id || plan.current || plan.isCustom) {
      return rejectWithValue("Selected plan cannot be updated.");
    }

    if (!state.currentClient?.id) {
      return rejectWithValue(
        "Unable to update plan because client information is unavailable.",
      );
    }

    const response = await updateBillingClientPlan(state.currentClient, plan.id);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to update plan.");
    }

    return {
      planId: Number(plan.id),
      planName: plan.name,
    };
  },
);

export const fetchPaymentMethodsThunk = createAsyncThunk(
  "billing/fetchPaymentMethods",
  async (clientId, { getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await getPaymentMethods(resolvedClientId, { limit: 20, offset: 0 });
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to load payment methods.");
    }

    return normalizePaymentMethods(response);
  },
);

export const addPaymentMethodThunk = createAsyncThunk(
  "billing/addPaymentMethod",
  async ({ payload, clientId }, { dispatch, getState, rejectWithValue }) => {
    const resolvedClientId = Number(clientId) || getRequiredCurrentUserClientId(getState());
    const response = await createPaymentMethod(payload, resolvedClientId);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to add payment method.");
    }

    await dispatch(fetchPaymentMethodsThunk(resolvedClientId));
    return true;
  },
);

export const fetchPaymentMethodDetailsThunk = createAsyncThunk(
  "billing/fetchPaymentMethodDetails",
  async (id, { rejectWithValue }) => {
    const response = await getPaymentMethodById(id);
    if (response?.error) {
      return rejectWithValue(
        response.error.message || "Unable to load payment method details.",
      );
    }

    return normalizePaymentMethodDetails(response);
  },
);

export const deletePaymentMethodThunk = createAsyncThunk(
  "billing/deletePaymentMethod",
  async (id, { rejectWithValue }) => {
    const response = await deletePaymentMethod(id);
    if (response?.error) {
      return rejectWithValue(response.error.message || "Unable to delete payment method.");
    }

    return id;
  },
);

const initialState = {
  currentPage: 0,
  billingCycle: "monthly",
  invoices: [],
  totalInvoices: 0,
  isInvoicesLoading: false,
  invoicesError: "",
  hasLoaded: false,
  plans: [],
  currentClient: null,
  currentPlanId: null,
  isLoadingPlans: false,
  isUpdatingPlanId: null,
  plansError: "",
  hasPlansLoaded: false,
  cards: [],
  selectedMethodDetails: null,
  isLoadingPaymentMethods: false,
  isLoadingDetails: false,
  isSubmittingPaymentMethod: false,
  isDeletingPaymentMethod: false,
  paymentMethodsError: "",
  paymentMethodDetailsError: "",
  hasPaymentMethodsLoaded: false,
};

export const billingInitialState = initialState;

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {
    setBillingCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setBillingCycle: (state, action) => {
      state.billingCycle = action.payload;
    },
    clearBillingMessages: (state) => {
      state.invoicesError = "";
      state.plansError = "";
      state.paymentMethodsError = "";
      state.paymentMethodDetailsError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoicesThunk.pending, (state) => {
        state.isInvoicesLoading = true;
        state.invoicesError = "";
      })
      .addCase(fetchInvoicesThunk.fulfilled, (state, action) => {
        state.isInvoicesLoading = false;
        state.invoices = action.payload.invoices;
        state.totalInvoices = action.payload.totalInvoices;
        state.hasLoaded = true;
      })
      .addCase(fetchInvoicesThunk.rejected, (state, action) => {
        state.isInvoicesLoading = false;
        state.invoicesError = action.payload || "Unable to load invoices.";
        state.invoices = [];
        state.totalInvoices = 0;
        state.hasLoaded = true;
      })
      .addCase(fetchBillingPlansThunk.pending, (state) => {
        state.isLoadingPlans = true;
        state.plansError = "";
      })
      .addCase(fetchBillingPlansThunk.fulfilled, (state, action) => {
        state.isLoadingPlans = false;
        state.plans = action.payload.plans;
        state.currentClient = action.payload.currentClient;
        state.currentPlanId = action.payload.currentPlanId;
        state.hasPlansLoaded = true;
        state.plansError = action.payload.clientError || "";
      })
      .addCase(fetchBillingPlansThunk.rejected, (state, action) => {
        state.isLoadingPlans = false;
        state.plansError = action.payload || "Unable to load subscription plans.";
        state.plans = [];
        state.hasPlansLoaded = true;
      })
      .addCase(updateBillingPlanThunk.pending, (state, action) => {
        state.isUpdatingPlanId = action.meta.arg?.id || null;
        state.plansError = "";
      })
      .addCase(updateBillingPlanThunk.fulfilled, (state, action) => {
        state.currentPlanId = action.payload.planId;
        state.isUpdatingPlanId = null;
        if (state.currentClient) {
          state.currentClient = {
            ...state.currentClient,
            plan_id: action.payload.planId,
          };
        }
      })
      .addCase(updateBillingPlanThunk.rejected, (state, action) => {
        state.isUpdatingPlanId = null;
        state.plansError = action.payload || "Unable to update plan.";
      })
      .addCase(fetchPaymentMethodsThunk.pending, (state) => {
        state.isLoadingPaymentMethods = true;
        state.paymentMethodsError = "";
      })
      .addCase(fetchPaymentMethodsThunk.fulfilled, (state, action) => {
        state.isLoadingPaymentMethods = false;
        state.cards = action.payload;
        state.hasPaymentMethodsLoaded = true;
      })
      .addCase(fetchPaymentMethodsThunk.rejected, (state, action) => {
        state.isLoadingPaymentMethods = false;
        state.paymentMethodsError = action.payload || "Unable to load payment methods.";
        state.cards = [];
        state.hasPaymentMethodsLoaded = true;
      })
      .addCase(addPaymentMethodThunk.pending, (state) => {
        state.isSubmittingPaymentMethod = true;
        state.paymentMethodsError = "";
      })
      .addCase(addPaymentMethodThunk.fulfilled, (state) => {
        state.isSubmittingPaymentMethod = false;
      })
      .addCase(addPaymentMethodThunk.rejected, (state, action) => {
        state.isSubmittingPaymentMethod = false;
        state.paymentMethodsError = action.payload || "Unable to add payment method.";
      })
      .addCase(fetchPaymentMethodDetailsThunk.pending, (state) => {
        state.isLoadingDetails = true;
        state.paymentMethodDetailsError = "";
        state.selectedMethodDetails = null;
      })
      .addCase(fetchPaymentMethodDetailsThunk.fulfilled, (state, action) => {
        state.isLoadingDetails = false;
        state.selectedMethodDetails = action.payload;
      })
      .addCase(fetchPaymentMethodDetailsThunk.rejected, (state, action) => {
        state.isLoadingDetails = false;
        state.paymentMethodDetailsError =
          action.payload || "Unable to load payment method details.";
      })
      .addCase(deletePaymentMethodThunk.pending, (state) => {
        state.isDeletingPaymentMethod = true;
        state.paymentMethodsError = "";
      })
      .addCase(deletePaymentMethodThunk.fulfilled, (state, action) => {
        state.isDeletingPaymentMethod = false;
        state.cards = state.cards.filter((item) => item.id !== action.payload);
      })
      .addCase(deletePaymentMethodThunk.rejected, (state, action) => {
        state.isDeletingPaymentMethod = false;
        state.paymentMethodsError = action.payload || "Unable to delete payment method.";
      });
  },
});

export const { clearBillingMessages, setBillingCurrentPage, setBillingCycle } =
  billingSlice.actions;
export const selectBillingState = (state) => state.billing;

export default billingSlice.reducer;
